import type { CodingScreenState } from '../interface/state';

/**
 * Own-key lookup into `state.values`. Deliberately uses
 * `Object.prototype.hasOwnProperty.call(...)`, NOT the `in` operator or truthiness, so:
 *  - inherited keys (`constructor`, `__proto__`, `toString`, ...) resolve to `undefined` instead of
 *    resolving through the prototype chain to `Object.prototype`'s own members (a security/
 *    correctness requirement -- `values` is `Readonly<Record<string, unknown>>` sourced from
 *    config-driven `contentId`s, which must never be trusted to safely index via `in`);
 *  - an absent `contentId` key also resolves to `undefined`, matching `in`'s negative case;
 *  - an OWN key whose value is falsy/empty (including an empty-string key itself) is still
 *    correctly addressable, unlike a truthiness check (`state.values[contentId] ||
 *    undefined`), which would wrongly collapse a real `''`/`0`/`false` stored value to `undefined`.
 */
export function readState(
  state: CodingScreenState | undefined,
  contentId: string | undefined
): unknown {
  return contentId !== undefined &&
    state?.values &&
    // biome-ignore lint/suspicious/noPrototypeBuiltins: task-2 brief pins this exact own-key form verbatim (vs. the equally-safe Object.hasOwn())
    Object.prototype.hasOwnProperty.call(state.values, contentId)
    ? state.values[contentId]
    : undefined;
}
